import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildShortcutMap } from '@components/layout/navStructure';
import { useCurrentProgramSlug, useCurrentCampaign } from '@state/useCurrentProgramSlug';

const CHORD_WINDOW_MS = 1500;

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

/**
 * Linear/Notion-style "g + letter" navigation chords.
 *
 * Press `g`, then within 1.5 s press a second key — the page jumps.
 *
 * The letter→route map is derived from navStructure.ts (buildShortcutMap) —
 * the same source Nav.tsx renders — so every quick key shown in the sidebar
 * is exactly the chord that fires here. All 11 nav items have a chord.
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  // Keyboard chords (g d / g e / etc.) target the *current* campaign rather
  // than always /fobt — matches the sidebar Nav resolution.
  const programPrefix = `/${useCurrentProgramSlug()}`;
  // Honor per-campaign hidden pages so a chord can't jump to a hidden page.
  // Joined to a string so the effect dep is stable across renders.
  const hiddenKey = (useCurrentCampaign()?.hiddenPages ?? []).join('|');

  useEffect(() => {
    let lastG = 0;
    const map = buildShortcutMap(programPrefix, hiddenKey ? hiddenKey.split('|') : []);

    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const k = e.key.toLowerCase();
      const now = performance.now();

      if (lastG && now - lastG <= CHORD_WINDOW_MS) {
        const target = map[k];
        lastG = 0;
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }

      if (k === 'g') {
        lastG = now;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, programPrefix, hiddenKey]);
}
